import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Map, Filter } from './components';
import Reload from '../../components/Reload/Reload';
import Api from 'services/Api';

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const parseCoordinate = (value) => {
  const coordinate = Number.parseFloat(value);
  return Number.isFinite(coordinate) ? Number(coordinate.toFixed(7)) : null;
};

const normalizeMonth = (value) => {
  const month = Number(value);
  return Number.isInteger(month) && month >= 1 && month <= 12 ? month : null;
};

const extractValidMonths = (data = []) => (
  [...new Set(data.map((item) => normalizeMonth(item?.mes)).filter((month) => month !== null))]
    .sort((a, b) => a - b)
);

const getCountryName = (item) => (
  item?.pais ||
  item?.country ||
  'País não informado'
);

const getStateName = (item) => (
  item?.estado ||
  item?.state ||
  item?.sigla ||
  'Local sem identificação'
);

const resolveCoordinates = (item) => {
  const coordinateCandidates = [
    [item?.lat, item?.lng],
    [item?.latitude, item?.longitude],
    [item?.latitude, item?.lng],
    [item?.lat, item?.longitude]
  ];

  for (const [latValue, lngValue] of coordinateCandidates) {
    const lat = parseCoordinate(latValue);
    const lng = parseCoordinate(lngValue);

    if (
      lat !== null &&
      lng !== null &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    ) {
      return { lat, lng };
    }
  }

  return null;
};

const getLocationCode = (item) => (
  item?.sigla ||
  item?.uf ||
  item?.code ||
  item?.countryCode ||
  ''
);

const MapsMeasures = () => {
  
  const user = JSON.parse(localStorage.getItem('userData')) || {};
  const canAccess =
    user?.permission?.maps?.read === true ||
    user?.permission?.mapsunits?.read === true;
  if (!canAccess) {
    window.location.replace('/home');
  }

  const [zoom] = useState(5);
  const [allPoints, setAllPoints] = useState([]);
  const [points, setPoints] = useState([]);
  const [mapPoints, setMapPoints] = useState(false);
  const [center] = useState({ lat: -14.8227361, lng: -54.1549677 });
  const [companyNames, setCompanyNames] = useState([]);
  const [grains, setGrains] = useState([]);
  const [years, setYears] = useState([]);
  const [months, setMonths] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [rawData, setRawData] = useState([]); // Armazena dados brutos da API
  const [selectedYear, setSelectedYear] = useState(null);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [selectedState, setSelectedState] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedGrain, setSelectedGrain] = useState('');
  const [selectedStatePanel, setSelectedStatePanel] = useState(null);

  const [darkMode, setDarkMode] = useState(() => {
    // Verifica se existe uma preferência salva no localStorage
    const savedTheme = localStorage.getItem('theme');
    return savedTheme ? savedTheme === 'dark' : false;
  });

  let [loading, setLoading] = useState(true);

  // Atualiza meses disponíveis baseado em Ano e filtros vigentes (Estado/Grão)
  const updateAvailableMonths = useCallback((year) => {
    if (!year || !rawData.length) {
      setAvailableMonths([]);
      return;
    }

    let data = rawData.filter(item => item.ano === year);
    if (selectedCountry) data = data.filter(item => getCountryName(item) === selectedCountry);
    if (selectedState) data = data.filter(item => getStateName(item) === selectedState);
    if (selectedGrain) data = data.filter(item => item.grain === selectedGrain);

    const uniqueMonths = extractValidMonths(data);
    setAvailableMonths(uniqueMonths);

    // Se o mês selecionado atual não existir mais, limpa a seleção
    if (selectedMonth && !uniqueMonths.includes(selectedMonth)) {
      setSelectedMonth('');
    }
  }, [rawData, selectedCountry, selectedState, selectedGrain, selectedMonth]);

  // Memoizar função de processamento de dados
  const computePoints = useCallback((data) => {
    const stateData = data.reduce((acc, item) => {
      const coords = resolveCoordinates(item);
      if (!coords) return acc;

      const countryName = getCountryName(item);
      const stateName = getStateName(item);
      const locationCode = getLocationCode(item);
      const key = `${countryName}:${stateName}:${coords.lat}:${coords.lng}:${locationCode}`;

      if (!acc[key]) {
        acc[key] = {
          pais: countryName,
          estado: stateName,
          sigla: locationCode,
          locationKey: key,
          totalMeasurements: 0,
          grainAverages: {},
          lat: coords.lat,
          lng: coords.lng
        };
      }

      acc[key].totalMeasurements += item.quantidade;

      if (!acc[key].grainAverages[item.grain]) {
        acc[key].grainAverages[item.grain] = {
          humidity: 0,
          count: 0
        };
      }

      const grainData = acc[key].grainAverages[item.grain];
      const prevCount = grainData.count;
      const newCount = prevCount + item.quantidade;

      // Calcula apenas a média de umidade
      grainData.humidity = ((grainData.humidity * prevCount) + (item.mediaUmidade * item.quantidade)) / newCount;
      grainData.count = newCount;

      return acc;
    }, {});

    // Cria pontos para o mapa
    const points = Object.values(stateData).map((pointData) => {
      return {
        type: "Feature",
        properties: {
          cluster: false,
          data: {
            ...pointData
          }
        },
        geometry: {
          type: "Point",
          coordinates: [pointData.lng, pointData.lat]
        }
      };
    }).filter(Boolean);

    return points;
  }, []);

  // Memoizar função de processamento de dados
  const processMapPoints = useCallback((data) => {
    const points = computePoints(data);

    setAllPoints(points);
    setPoints(points);
    setMapPoints(true);

    return points; // Retorna os pontos processados
  }, [computePoints]);

  // Memoizar cálculo dos filtros disponíveis
  const getAvailableFilters = useMemo(() => (data, currentYear, currentMonth, currentCountry, currentState, currentGrain) => {
    // Anos sempre ficam disponíveis
    const allYears = [...new Set(data.map(item => item.ano))].sort((a, b) => b - a);
    
    // Bases de dados para compor opções de filtros de forma consistente
    const dataYear = currentYear ? data.filter(item => item.ano === currentYear) : data;
    const dataYearMonth = currentMonth ? dataYear.filter(item => item.mes === currentMonth) : dataYear;

    // Months: dependem de Ano + (País?) + (Estado?) + (Grão?) mas independem do mês atual
    const dataYearCountry = currentCountry ? dataYear.filter(item => getCountryName(item) === currentCountry) : dataYear;
    const dataYearCountryState = currentState ? dataYearCountry.filter(item => getStateName(item) === currentState) : dataYearCountry;
    const dataYearCountryStateGrain = currentGrain ? dataYearCountryState.filter(item => item.grain === currentGrain) : dataYearCountryState;
    const months = extractValidMonths(dataYearCountryStateGrain);

    // Countries: dependem de Ano + (Mês?) + (Grão?) mas independem do país atual
    const dataYearMonthGrain = currentGrain ? dataYearMonth.filter(item => item.grain === currentGrain) : dataYearMonth;
    const countries = [...new Set(dataYearMonthGrain.map(item => getCountryName(item)).filter(Boolean))].sort();

    // States: dependem de Ano + (Mês?) + (País?) + (Grão?) mas independem do Estado atual
    const dataYearMonthCountry = currentCountry ? dataYearMonthGrain.filter(item => getCountryName(item) === currentCountry) : dataYearMonthGrain;
    const states = [...new Set(dataYearMonthCountry.map(item => getStateName(item)).filter(Boolean))].sort();

    // Grains: dependem de Ano + (Mês?) + (País?) + (Estado?) mas independem do Grão atual
    const dataYearMonthCountryState = currentState ? dataYearMonthCountry.filter(item => getStateName(item) === currentState) : dataYearMonthCountry;
    const grains = [...new Set(dataYearMonthCountryState.map(item => item.grain))].sort();

    return { years: allYears, months, countries, states, grains };
  }, []);

  // Função atualizada para filtrar pontos
  const filterPoints = () => {
    if (!rawData.length) return;

    let filtered = rawData;

    // Aplica filtros em sequência
    if (selectedYear) {
      filtered = filtered.filter(item => item.ano === selectedYear);
    }
    if (selectedMonth && filtered.some(item => item.mes === selectedMonth)) {
      filtered = filtered.filter(item => item.mes === selectedMonth);
    }
    if (selectedCountry && filtered.some(item => getCountryName(item) === selectedCountry)) {
      filtered = filtered.filter(item => getCountryName(item) === selectedCountry);
    }
    if (selectedState && filtered.some(item => getStateName(item) === selectedState)) {
      filtered = filtered.filter(item => getStateName(item) === selectedState);
    }
    if (selectedGrain && filtered.some(item => item.grain === selectedGrain)) {
      filtered = filtered.filter(item => item.grain === selectedGrain);
    }

    // Se não houver dados após a filtragem, mantém Ano/Mês e limpa apenas filtros dependentes
    if (filtered.length === 0) {
      setSelectedCountry('');
      setSelectedState('');
      setSelectedGrain('');
      filtered = rawData.filter(item =>
        (!selectedYear || item.ano === selectedYear) &&
        (!selectedMonth || item.mes === selectedMonth)
      );
    }

    // Processa os pontos para o mapa (conjunto completo segundo os filtros)
    processMapPoints(filtered);

    // Atualiza o painel se estiver aberto
    if (selectedStatePanel) {
      // Em vez de reprocessar o mapa e sobrescrever 'points', apenas
      // recalcula localmente os dados do estado selecionado
      const recalculatedPoints = computePoints(filtered);
      const updatedStateData = recalculatedPoints.find(point => 
        point.properties.data.locationKey === selectedStatePanel.locationKey
      );

      if (!updatedStateData) {
        // Se não houver dados para o estado selecionado, fecha o painel
        setSelectedStatePanel(null);
      } else {
        setSelectedStatePanel(updatedStateData.properties.data);
      }
    }
  };

  // Handlers atualizados para os filtros
  const handleCountryChange = (country) => {
    if (!country || rawData.some(item =>
      getCountryName(item) === country &&
      (!selectedYear || item.ano === selectedYear) &&
      (!selectedMonth || item.mes === selectedMonth)
    )) {
      setSelectedCountry(country);

      if (country && selectedState) {
        const stateStillExists = rawData.some(item =>
          getCountryName(item) === country &&
          getStateName(item) === selectedState &&
          (!selectedYear || item.ano === selectedYear) &&
          (!selectedMonth || item.mes === selectedMonth)
        );

        if (!stateStillExists) {
          setSelectedState('');
        }
      }

      if (selectedYear) updateAvailableMonths(selectedYear);
    }
  };

  const handleStateChange = (state) => {
    if (!state || rawData.some(item => 
      getStateName(item) === state &&
      (!selectedCountry || getCountryName(item) === selectedCountry) &&
      (!selectedYear || item.ano === selectedYear) &&
      (!selectedMonth || item.mes === selectedMonth)
    )) {
      setSelectedState(state);
      // Se um ano estiver selecionado, atualiza os meses disponíveis levando em conta o novo estado
      if (selectedYear) updateAvailableMonths(selectedYear);
    }
  };

  const handleMonthChange = (month) => {
    if (!month || rawData.some(item => 
      item.mes === month && 
      (!selectedYear || item.ano === selectedYear)
    )) {
      setSelectedMonth(month);
    }
  };

  const handleGrainChange = (grain) => {
    if (!grain || rawData.some(item => 
      item.grain === grain &&
      (!selectedYear || item.ano === selectedYear) &&
      (!selectedMonth || item.mes === selectedMonth) &&
      (!selectedCountry || getCountryName(item) === selectedCountry) &&
      (!selectedState || getStateName(item) === selectedState)
    )) {
      setSelectedGrain(grain);
      // Se um ano estiver selecionado, atualiza os meses disponíveis levando em conta o novo grão
      if (selectedYear) updateAvailableMonths(selectedYear);
    }
  };

  // Handler para mudança de ano
  const handleYearChange = (year) => {
    setSelectedYear(year);
    if (!year) {
      const allMonths = extractValidMonths(rawData);
      setAvailableMonths(allMonths);
      if (selectedMonth && !allMonths.includes(selectedMonth)) setSelectedMonth('');
    } else {
      updateAvailableMonths(year);
    }
  };

  // Adicionar função para limpar filtros
  const handleClearFilters = () => {
    if (!rawData.length) {
      setSelectedYear(null);
      setSelectedMonth('');
      setSelectedCountry('');
      setSelectedState('');
      setSelectedGrain('');
      setAvailableMonths([]);
      setCountries([]);
      setStates([]);
      setGrains([]);
      setSelectedStatePanel(null);
      return;
    }

    // Encontra o ano e mês mais recente nos dados brutos
    const sortedData = [...rawData].sort((a, b) => {
      if (a.ano !== b.ano) return b.ano - a.ano;
      return b.mes - a.mes;
    });

    const latestEntry = sortedData[0];
    if (!latestEntry) return;
    const latestYear = latestEntry.ano;
    
    // Obtém meses disponíveis para o ano mais recente
    const monthsForLatestYear = rawData
      .filter(item => item.ano === latestYear)
      .map(item => item.mes);
    const uniqueMonths = [...new Set(monthsForLatestYear.map((month) => normalizeMonth(month)).filter((month) => month !== null))].sort((a, b) => a - b);
    const latestMonth = uniqueMonths.length ? uniqueMonths[uniqueMonths.length - 1] : '';

    // Reseta para os valores iniciais
    setSelectedYear(latestYear);
    setSelectedMonth(latestMonth);
    setSelectedCountry('');
    setSelectedState('');
    setSelectedGrain('');
    
    // Atualiza os filtros disponíveis
    const initialFilters = getAvailableFilters(rawData, latestYear, latestMonth, null, null, null);
    setAvailableMonths(uniqueMonths);
    setCountries(initialFilters.countries);
    setStates(initialFilters.states);
    setGrains(initialFilters.grains);

    // Fecha o painel de médias
    setSelectedStatePanel(null);
  };

  useEffect(() => {
    // Monitora mudanças de tema
    const handleThemeChange = () => {
      const isDark = document.body.classList.contains('dark-theme');
      setDarkMode(isDark);
    };

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          handleThemeChange();
        }
      });
    });
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await Api.get('/api/v1/measurements-average');
        const normalizedData = (Array.isArray(data) ? data : [])
          .map((item) => ({
            ...item,
            ano: Number(item?.ano),
            mes: normalizeMonth(item?.mes)
          }))
          .filter((item) => Number.isFinite(item.ano) && item.mes !== null);

        setRawData(normalizedData);

        if (!normalizedData.length) {
          setSelectedYear(null);
          setSelectedMonth('');
          setAvailableMonths([]);
          setCountries([]);
          setStates([]);
          setGrains([]);
          setYears([]);
          setMonths([]);
          setAllPoints([]);
          setPoints([]);
          setMapPoints(false);
          setLoading(false);
          return;
        }
        
        // Encontra o ano e mês mais recente
        const sortedData = [...normalizedData].sort((a, b) => {
          if (a.ano !== b.ano) return b.ano - a.ano;
          return b.mes - a.mes;
        });

        const latestEntry = sortedData[0];
        if (!latestEntry) {
          setLoading(false);
          return;
        }
        const latestYear = latestEntry.ano;

        // Define o ano inicial
        setSelectedYear(latestYear);

        // Obtém todos os meses disponíveis para o ano mais recente
        const monthsForLatestYear = normalizedData
          .filter(item => item.ano === latestYear)
          .map(item => item.mes);

        // Remove duplicatas e ordena os meses
        const uniqueMonths = [...new Set(monthsForLatestYear.map((month) => normalizeMonth(month)).filter((month) => month !== null))].sort((a, b) => a - b);
        
        // Define o mês mais recente
        const latestMonth = uniqueMonths.length ? uniqueMonths[uniqueMonths.length - 1] : '';
        setSelectedMonth(latestMonth);
        setAvailableMonths(uniqueMonths);

        // Obtém os filtros disponíveis iniciais
        const initialFilters = getAvailableFilters(normalizedData, latestYear, latestMonth, null, null, null);
        
        setCountries(initialFilters.countries);
        setStates(initialFilters.states);
        setGrains(initialFilters.grains);
        setYears(initialFilters.years);
        setMonths(uniqueMonths); // Usa os meses já processados
        
        // Processa pontos iniciais do mapa usando o ano e mês mais recente
        processMapPoints(normalizedData.filter(item => 
          item.ano === latestYear && 
          item.mes === latestMonth
        ));

        setLoading(false);
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  // Melhorar performance do useEffect
  useEffect(() => {
    if (!rawData.length) return;

    const debounceTimeout = setTimeout(() => {
      const availableFilters = getAvailableFilters(
        rawData,
        selectedYear,
        selectedMonth,
        selectedCountry,
        selectedState,
        selectedGrain
      );

  setYears(availableFilters.years);
  setMonths(availableFilters.months);
  setCountries(availableFilters.countries);
  setStates(availableFilters.states);
  setGrains(availableFilters.grains);
  setAvailableMonths(availableFilters.months);

      filterPoints();
    }, 100); // Pequeno debounce para evitar múltiplas atualizações

    return () => clearTimeout(debounceTimeout);
  }, [selectedYear, selectedMonth, selectedCountry, selectedState, selectedGrain, rawData]);

  return (
    <div
      className="map-container"
      style={{
        width: '100%',
        height: '100vh',
        backgroundColor: 'var(--map-background)',
        position: 'relative'
      }}
    >
      {loading && <Reload />}

      <Filter
        filter={{
          years,
          availableMonths,
          selectedYear,
          selectedCountry,
          selectedState,
          selectedMonth,
          selectedGrain,
          countries,
          states,
          grains,
          onYearChange: handleYearChange,
          onCountryChange: handleCountryChange,
          onStateChange: handleStateChange,
          onMonthChange: handleMonthChange,
          onGrainChange: handleGrainChange,
          onClearFilters: handleClearFilters // Nova prop
        }}
        companyNames={companyNames}
        count={points.length}
        darkMode={darkMode}
      />

      <Map
        center={center}
        containerElement={<div style={{ height: '100%' }} />}
        googleMapURL={`https://maps.googleapis.com/maps/api/js?v=3.exp&libraries=geometry,drawing,places&key=${GOOGLE_API_KEY}&language=pt-br`}
        loadingElement={<div style={{ height: '100%' }} />}
        mapElement={<div style={{ height: '100%' }} />}
        mapPoints={mapPoints}
        zoom={zoom}
        points={points}
        darkMode={darkMode}
        selectedState={selectedStatePanel}
        setSelectedState={setSelectedStatePanel}
      />
    </div>
  );
};
export default MapsMeasures;
